import { Button, Card, Flex } from '@aws-amplify/ui-react';
import { useContext, useEffect, useState } from 'react';
import ReactModal from 'react-modal';
import 'react-querybuilder/dist/query-builder.css';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import type { Edulead } from 'edulead';
import Banner from 'src/custom-components/Banner';
import NA from 'src/custom-components/NA';
import IngestionExportModal from '../custom-components/IngestionExportModal';
import LoadingBars from '../custom-components/LoadingBars';
import { GlobalContext, GlobalContextProps } from '../global-context';
import { eduleadV4 } from '../services/edulead-v4-client';
import { toastV4Error } from '../services/edulead-v4';
import { useIngestionExport } from '../hooks/useIngestionExport';
import General from '../utils/GeneralUtils';
import { IngestionsSearchParams } from '../utils/IngestionsSearchParams';
import { convertQtoQuery } from '../utils/queryConverter';

const PAGE_SIZE = 100

/** One visitor: the visits of one IP, with the location and nearby institutions of its newest visit. */
type VisitorGroup = {
  ip: string;
  geo?: Edulead.EduleadV4VisitGeo;
  institutions?: Edulead.EduleadV4VisitInstitution[];
  visits: Edulead.EduleadV4Visit[];
}

/** Groups visits by IP in order of first appearance, as the legacy API grouped a page; visits without an IP are dropped. */
function groupByIp(visits: Edulead.EduleadV4Visit[]): VisitorGroup[] {
  const groups = new Map<string, VisitorGroup>();
  for (const visit of visits) {
    if (!visit.ip) continue;
    const group = groups.get(visit.ip) ?? { ip: visit.ip, geo: visit.geo, institutions: visit.institutions, visits: [] };
    group.visits.push(visit);
    groups.set(visit.ip, group);
  }
  return [...groups.values()];
}

export type TrackingInterval = {
  label: string;
  from: string;
  to: string;
}

export function Tracking(props: any) {

  const globalContext = useContext(GlobalContext);
  General.setTitle(document, 'Tracking', globalContext);

  const location = useLocation();
  const navigate = useNavigate();

  const { id } = useParams()
  const [uid] = useState(id);

  const [isLoadingIngestions, setIsLoadingIngestions] = useState(true);
  const [visits, setVisits] = useState<Edulead.EduleadV4Visit[]>([]);
  const [totalSize, setTotalSize] = useState(0);
  const [conditions, setConditions] = useState('');
  const [nextPageToken, setNextPageToken] = useState('');
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentSearchParams, setCurrentSearchParams] = useState<IngestionsSearchParams>();
  const [availableIntervals, setAvailableIntervals] = useState<TrackingInterval[]>([]);
  const [site, setSite] = useState<Edulead.EduleadV4Site | null>(null);
  const [isLoadingDomain, setIsLoadingDomain] = useState(true);
  const [isShowTrackingScriptModalOpened, setIsShowTrackingScriptModalOpened] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  
  // Use the ingestion export hook
  const { exportIngestions, isExporting, exportResponse, exportError } = useIngestionExport();

  async function loadIngestions() {
    try {
      if (!uid) {
        console.error('No site ID provided');
        return;
      }
      setIsLoadingIngestions(true)
      const query = [...convertQtoQuery(currentSearchParams?.q.value || ''), { field: 'siteId', operator: 'equals', value: uid }];
      const searchConditions = JSON.stringify(query);
      const page = await eduleadV4.visits.search({
        conditions: searchConditions,
        order_by: 'create_time desc',
        page_size: PAGE_SIZE,
      })
      setConditions(searchConditions)
      setVisits(page.data)
      setTotalSize(Number(page.response.total_size ?? 0))
      setNextPageToken(page.response.next_page_token ?? '')
      setAvailableIntervals(generatePastWeeks(12));
    } catch (err) {
      setVisits([])
      setNextPageToken('')
      toastV4Error(err, 'Something went wrong loading tracking data')
    } finally {
      setIsLoadingIngestions(false)
    }
  }

  async function loadMoreVisits() {
    if (!nextPageToken || isLoadingMore) return
    try {
      setIsLoadingMore(true)
      const page = await eduleadV4.visits.search({
        conditions,
        order_by: 'create_time desc',
        page_size: PAGE_SIZE,
        page_token: nextPageToken,
      })
      setVisits((prev) => [...prev, ...page.data])
      setNextPageToken(page.response.next_page_token ?? '')
    } catch (err) {
      toastV4Error(err, 'Something went wrong loading tracking data')
    } finally {
      setIsLoadingMore(false)
    }
  }

  async function getTeamDomain() {
    try {
      if (!uid || !globalContext.authInfo?.teamId) return
      setIsLoadingDomain(true)
      const response = await eduleadV4.sites.get({ id: uid })
      setSite(response.site ?? null)
    } catch (err) {
      toastV4Error(err, 'Something went wrong loading domain')
    } finally {
      setIsLoadingDomain(false)
    }
  }

  function generatePastWeeks(nrWeeks: number): { label: string; from: string; to: string }[] {
    const result = [];
    const now = new Date();
  
    for (let i = 0; i < nrWeeks; i++) {
      // Calculate the Monday of the week, going back i weeks
      const current = new Date(now);
      current.setDate(now.getDate() - now.getDay() + 1 - i * 7); // Monday as start of the week
  
      const from = new Date(current.getFullYear(), current.getMonth(), current.getDate(), 0, 0, 0, 0);
      const to = new Date(from);
      to.setDate(to.getDate() + 6);
      to.setHours(23, 59, 59, 999);
  
      result.push({
        label: `${from.toLocaleDateString('default', { month: 'short', day: 'numeric' })} - ${to.toLocaleDateString('default', { month: 'short', day: 'numeric' })}`, // e.g. "Jun 24 2024"
        from: from.toISOString(),
        to: to.toISOString(),
      });
    }
  
    return result
  }

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const params = {
      page: General.getQueryParamValueOrNull(queryParams, 'page') || 1,
      sortBy: General.getQueryParamValueOrNull(queryParams, 'sortBy') || 'ingestedAt',
      sortDir: General.getQueryParamValueOrNull(queryParams, 'sortDir') || 'desc',
      q: General.getQueryParamValueOrNull(queryParams, 'q') || General.getQ({ rules: [] }),
    }
    setCurrentSearchParams(new IngestionsSearchParams(params))
  }, [location.search]);

  function handleOnChangePage(q: string) {
    const updatedSearchParams = new IngestionsSearchParams({
      ...currentSearchParams?.raw(),
      ...{ q }
    });
    navigate(`?${updatedSearchParams?.serialize('1')}`);
  }

  useEffect(() => {
    if (!currentSearchParams) return
    if (!globalContext.authInfo?.teamId) return
    loadIngestions()
    getTeamDomain()
  }, [currentSearchParams, globalContext.authInfo?.teamId]);

  function getScriptContent(siteId: string) {
    return `&lt;script&gt;
  var eduleadSiteId = &#x27;${siteId}&#x27;;
  (function() {
    const scriptName = &#x27;edulead-script&#x27;;
    if (document.getElementById(scriptName)) return;
    const script = document.createElement(&#x27;script&#x27;);
    script.id = scriptName; script.async = true; script.crossOrigin = &#x27;anonymous&#x27;;
    script.src = &#x27;https://cdn.getedulead.com/tag.js&#x27;;
    document.head.appendChild(script);
  })();
&lt;/script&gt;`
  }

  function showTrackingScript() {
    setIsShowTrackingScriptModalOpened(true)
  }

  const handleExportConfirm = async (format: any, timeRange: any, stateFilter: string | null) => {
    if (uid) {
      await exportIngestions(uid, globalContext.authInfo?.teamId!, format, timeRange, stateFilter);
    }
  };

  return <GlobalContext.Consumer>
    {
      (globalContextProps: GlobalContextProps) => <>
        <div className={`content content-1000`}>
          <Flex alignItems="center" justifyContent="space-between" style={{ marginBottom: 0 }}>
            <h2 style={{ margin: 0, marginBottom: 0 }}><Link to='/tracking'>Tracking</Link> / {site?.domain}</h2>
            <div>
              <Button
                size="small"
                className="tiny"
                onClick={() => {
                  showTrackingScript()
                }}
              >
                Show Tracking Script
              </Button>
              <Button
                size="small"
                className="tiny"
                onClick={() => setIsExportModalOpen(true)}
                style={{ marginLeft: 10 }}
              >
                Export
              </Button>
            </div>
          </Flex>
          {
            !isLoadingIngestions
              ? !visits.length && site?.id
                ? <>
                    <Banner color="blue">
                      Track which districts are visiting your website with Edulead. Simply add this script to every page of your website, and return here to view the results.
                    </Banner>
                    <div style={{ marginTop: 20, marginBottom: 20 }}>
                      <pre className='code'><code dangerouslySetInnerHTML={{ __html: getScriptContent(site.id) }} /></pre>
                    </div>
                  </>
                : <></>
              : <></>
          }
        </div>
        <div className={`content content-1000 tracking-intervals-available`}>
          {
            visits.length
              ? <div className='tracking-intervals'>
                  {
                    availableIntervals.map((it) => {
                      return <div
                        className={`tracking-interval ${currentSearchParams ? 'tracking-interval-active' : ''}`}
                        onClick={() => {
                          const q = General.getQ({
                            rules: [{
                              field: 'ingestedAt',
                              operator: 'between_string',
                              value: `${it.from},${it.to}`
                            }, {
                              field: 'siteId',
                              operator: 'equals',
                              value: uid
                            }],
                            combinator: 'and'
                          });
                          handleOnChangePage(q)
                        }}
                      >
                        { it.label }
                      </div>
                    })
                  }
                </div>
              : <></>
          }
        </div>
        <div className={`content content-1000`}>
          {
            isLoadingIngestions
              ? <LoadingBars numberOfRows={8} />
              : visits.length > 0
                ? <>
                    <div className='tracking-results'>
                      {
                        groupByIp(visits).map((item) => {
                          return <div className='tracking-result-item' key={item.ip}>
                            <div className='tracking-result-item-head'>
                              {/* <div> */}
                                <Flex className='tracking-result-location' justifyContent='space-between' direction='row' style={{ width: '100%' }}>
                                  <div>
                                    Visitor from { [item.geo?.city, item.geo?.region_code, item.geo?.country?.replace('United States', '')].filter(Boolean).join(', ') || <NA text='Unknown Location' /> }
                                  </div>
                                  {
                                    item.institutions?.[0]
                                      ? <Link style={{ marginLeft: 10 }} to={`/directory/institutions/${item.institutions[0].institution_id}`}>{ item.institutions[0].name } ({ General.kmDistanceToMilesAway(item.institutions[0].distance_km ?? 0) }mi away)</Link>
                                      : <></>
                                  }
                                </Flex>
                                {/* <span className='tracking-result-location-desc' style={{ marginLeft: 10 }}>
                                  { [item?.visits?.[0]?.['browser']?.['name'], item?.visits?.[0]?.['browser']?.['version']].filter(it => !!it).join(', ') || '' }
                                </span>
                                <span className='tracking-result-location-desc' style={{ marginLeft: 10 }}>
                                  { [item?.visits?.[0]?.['os']?.['name'], item?.visits?.[0]?.['os']?.['version']].filter(it => !!it).join(', ') || '' }
                                </span>
                                <span className='tracking-result-location-desc' style={{ marginLeft: 10 }}>
                                  { [item?.visits?.[0]?.['device']?.['model'], item?.visits?.[0]?.['device']?.['vendor']].filter(it => !!it).join(', ') || '' }
                                </span> */}
                              {/* </div> */}
                            </div>
                            <div className='tracking-result-item-body'>
                              <div style={{ marginTop: 5 }}>
                                {
                                  item.visits.map((it) => (
                                    <div className='tracking-result-item-body-visit' key={it.id}>
                                      <div className='tracking-result-item-body-visit-time'>
                                        <span
                                          data-tooltip-content={ General.formatDateTime(it.create_time) }
                                          data-tooltip-id='main-tooltip'
                                        >
                                          { it.create_time ? General.daysAgoDetailed(it.create_time) : null }
                                        </span>
                                      </div>
                                      <div>
                                        {
                                          it.path === '/'
                                            ? 'Homepage'
                                            : General.ellipsisInMiddle(it.path, 60)
                                        }
                                      </div>
                                      {/* <span style={{ marginLeft: 20 }}>
                                        { General.ellipsisInMiddle(it?.['title'], 150) }
                                      </span> */}
                                    </div>
                                  ))
                                }
                              </div>
                            </div>
                          </div>
                        })
                      }
                    </div>
                  </>
                : <Card variation='outlined' className='desc-2' style={{ textAlign: 'center', marginTop: 30 }}>
                    No visits found for this time period
                  </Card>
          }

          {
            !isLoadingIngestions && visits.length > 0 &&
              <Flex
                marginTop={30}
                marginBottom={50}
                direction='column'
                alignItems='center'
              >
                <span className='desc-2'>Showing { visits.length.toLocaleString() } of { totalSize.toLocaleString() } visits</span>
                {
                  nextPageToken &&
                    <Button size="small" className="silent" onClick={loadMoreVisits} isLoading={isLoadingMore}>
                      Load More
                    </Button>
                }
              </Flex>
          }
        </div>
        <ReactModal
          isOpen={isShowTrackingScriptModalOpened}
          shouldCloseOnEsc={true}
          shouldCloseOnOverlayClick={true}
          onRequestClose={() => setIsShowTrackingScriptModalOpened(false)}
          style={General.getModalDefaults({ width: '800px' })}
          parentSelector={General.getModalRoot}
        >
          <h4 style={{ marginTop: 0 }}>Tracking Script</h4>
          <div className='desc-2' style={{ marginBottom: 20 }}>
            Copy and paste this script into every page of your website.
          </div>
          <pre className='code'><code dangerouslySetInnerHTML={{ __html: getScriptContent(site?.id ?? '') }} /></pre>
        </ReactModal>
        
        {/* Ingestion Export Modal */}
        <IngestionExportModal
          isOpen={isExportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
          onConfirm={handleExportConfirm}
          isExporting={isExporting}
          exportResponse={exportResponse}
          exportError={exportError}
          siteId={uid || ''}
        />
      </>
    }
  </GlobalContext.Consumer>
}
